/**
 * Signaler et bloquer (migration 0604 — App Review, Guideline 1.2).
 *
 * - Tout compte : signaler un message (discussion ou salle visio) ou un
 *   utilisateur (`report_content`, copie du message côté serveur, 30 par jour),
 *   bloquer / débloquer quelqu'un (`block_user` / `unblock_user`), voir ses
 *   blocages (`list_blocked_users`). Bloquer coupe la messagerie dans les deux
 *   sens, révoque le lien camarade et empêche tout nouveau lien ; les messages
 *   de la personne bloquée ne sont plus montrés (RLS).
 * - Admin : file de modération (`list_content_reports`) et traitement
 *   (`resolve_content_report` : masquer le message pour tous, ou classer).
 *
 * Tables postérieures aux types générés → client générique (cf. CLAUDE.md).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

const db = supabase as unknown as SupabaseClient;

export type ReportReason = 'harassment' | 'inappropriate' | 'spam' | 'impersonation' | 'other';

/** Ordre d'affichage = ordre des clés. Miroir du CHECK `content_reports.reason`. */
export const REPORT_REASON_LABELS: Record<ReportReason, string> = {
  harassment: 'Harcèlement ou moqueries',
  inappropriate: 'Contenu choquant ou inapproprié',
  spam: 'Spam ou publicité',
  impersonation: 'Se fait passer pour quelqu’un',
  other: 'Autre chose',
};

export const REPORT_DETAILS_MAX = 1000;

export type ReportContentType = 'discussion_message' | 'room_message' | 'user';

/** Ce qu'on signale : un message (discussion / salle visio) ou une personne. */
export type ReportTarget =
  | { kind: 'discussion_message' | 'room_message'; messageId: string; userId: string }
  | { kind: 'user'; userId: string };

export interface ReportInput {
  target: ReportTarget;
  reason: ReportReason;
  details?: string;
  /** Bloquer aussi l'auteur, dans le même geste. */
  block?: boolean;
}

/** Arguments de la RPC `report_content`. PUR. */
export function reportRpcArgs(input: ReportInput) {
  const details = (input.details ?? '').trim();
  return {
    p_content_type: input.target.kind,
    p_content_id: input.target.kind === 'user' ? null : input.target.messageId,
    p_target_user_id: input.target.userId,
    p_reason: input.reason,
    p_details: details ? details.slice(0, REPORT_DETAILS_MAX) : null,
    p_block: !!input.block,
  };
}

/** Erreur serveur → phrase affichable. PUR. */
export function translateSafetyError(message: string | undefined | null): string {
  const m = message ?? '';
  if (m.includes('report_rate_limited'))
    return 'Tu as déjà envoyé beaucoup de signalements aujourd’hui. Réessaie demain.';
  if (m.includes('cannot_report_self') || m.includes('invalid_target'))
    return 'Tu ne peux pas faire ça sur ton propre compte.';
  if (m.includes('content_not_found') || m.includes('user_not_found'))
    return 'Ce contenu n’existe plus.';
  if (m.includes('not_authenticated')) return 'Connecte-toi pour continuer.';
  if (m.includes('not_authorized')) return 'Action réservée aux admins.';
  return 'Action impossible pour le moment. Réessaie.';
}

export class SafetyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SafetyError';
  }
}

// ---------------------------------------------------------------------------
// Blocages
// ---------------------------------------------------------------------------

export interface BlockedUser {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  blockedAt: string;
}

interface RawBlockedUser {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  blocked_at: string;
}

export function blockedUserFromRow(row: RawBlockedUser): BlockedUser {
  return {
    userId: row.user_id,
    displayName: row.display_name?.trim() || 'Utilisateur',
    avatarUrl: row.avatar_url,
    blockedAt: row.blocked_at,
  };
}

export const blockedUsersKey = (userId: string | undefined) => ['blocked-users', userId] as const;

export function useBlockedUsers() {
  const { user } = useAuth();
  return useQuery({
    queryKey: blockedUsersKey(user?.id),
    enabled: !!user?.id && !user.is_anonymous,
    staleTime: 60_000,
    queryFn: async (): Promise<BlockedUser[]> => {
      const { data, error } = await db.rpc('list_blocked_users');
      if (error) throw new SafetyError(translateSafetyError(error.message));
      return ((data ?? []) as RawBlockedUser[]).map(blockedUserFromRow);
    },
  });
}

/** Ai-je bloqué cette personne ? (`undefined` tant que la liste charge.) */
export function useIsBlockedByMe(userId: string | null | undefined): boolean | undefined {
  const blocked = useBlockedUsers();
  if (!userId) return false;
  if (!blocked.data) return undefined;
  return blocked.data.some((b) => b.userId === userId);
}

/** Tout ce qu'un blocage change à l'écran : contacts, fils, demandes. */
function invalidateAfterBlockChange(qc: ReturnType<typeof useQueryClient>) {
  for (const key of [
    'blocked-users',
    'my-peers',
    'peer-requests',
    'discussions',
    'discussion',
    'discussion-messages',
  ]) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

export function useBlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await db.rpc('block_user', { p_user_id: userId });
      if (error) throw new SafetyError(translateSafetyError(error.message));
    },
    onSuccess: () => invalidateAfterBlockChange(qc),
  });
}

export function useUnblockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await db.rpc('unblock_user', { p_user_id: userId });
      if (error) throw new SafetyError(translateSafetyError(error.message));
    },
    onSuccess: () => invalidateAfterBlockChange(qc),
  });
}

// ---------------------------------------------------------------------------
// Signalements
// ---------------------------------------------------------------------------

export function useReportContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReportInput): Promise<string> => {
      const { data, error } = await db.rpc('report_content', reportRpcArgs(input));
      if (error) throw new SafetyError(translateSafetyError(error.message));
      return data as string;
    },
    onSuccess: (_id, input) => {
      if (input.block) invalidateAfterBlockChange(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// Modération (admin)
// ---------------------------------------------------------------------------

export type ContentReportStatus = 'open' | 'actioned' | 'dismissed';

export const CONTENT_REPORT_STATUS_LABELS: Record<ContentReportStatus, string> = {
  open: 'À traiter',
  actioned: 'Traité',
  dismissed: 'Classé sans suite',
};

export const CONTENT_TYPE_LABELS: Record<ReportContentType, string> = {
  discussion_message: 'Message (discussion)',
  room_message: 'Message (appel visio)',
  user: 'Utilisateur',
};

export interface ContentReport {
  id: string;
  contentType: ReportContentType;
  contentId: string | null;
  reason: ReportReason;
  details: string | null;
  excerpt: string | null;
  status: ContentReportStatus;
  contentHidden: boolean;
  adminNote: string | null;
  createdAt: string;
  resolvedAt: string | null;
  reporterId: string;
  reporterName: string;
  targetUserId: string | null;
  targetName: string;
  /** Nombre total de signalements visant la même personne. */
  targetReportCount: number;
}

export interface RawContentReport {
  id: string;
  content_type: ReportContentType;
  content_id: string | null;
  reason: ReportReason;
  details: string | null;
  content_excerpt: string | null;
  status: ContentReportStatus;
  content_hidden: boolean;
  admin_note: string | null;
  created_at: string;
  resolved_at: string | null;
  reporter_id: string;
  reporter_name: string | null;
  target_user_id: string | null;
  target_name: string | null;
  target_report_count: number | string | null;
}

export function contentReportFromRow(row: RawContentReport): ContentReport {
  return {
    id: row.id,
    contentType: row.content_type,
    contentId: row.content_id,
    reason: row.reason,
    details: row.details,
    excerpt: row.content_excerpt,
    status: row.status,
    contentHidden: row.content_hidden,
    adminNote: row.admin_note,
    createdAt: row.created_at,
    resolvedAt: row.resolved_at,
    reporterId: row.reporter_id,
    reporterName: row.reporter_name?.trim() || 'Utilisateur supprimé',
    targetUserId: row.target_user_id,
    targetName: row.target_name?.trim() || 'Utilisateur supprimé',
    targetReportCount: Number(row.target_report_count ?? 0),
  };
}

export type ContentReportFilter = 'open' | 'closed';

export function useContentReports(filter: ContentReportFilter, enabled: boolean) {
  return useQuery({
    queryKey: ['content-reports', filter],
    enabled,
    queryFn: async (): Promise<ContentReport[]> => {
      const { data, error } = await db.rpc('list_content_reports', { p_status: filter });
      if (error) throw new SafetyError(translateSafetyError(error.message));
      return ((data ?? []) as RawContentReport[]).map(contentReportFromRow);
    },
  });
}

export interface ResolveReportInput {
  reportId: string;
  status: ContentReportStatus;
  hideContent?: boolean;
  note?: string;
}

export function useResolveContentReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ResolveReportInput) => {
      const { error } = await db.rpc('resolve_content_report', {
        p_report_id: input.reportId,
        p_status: input.status,
        p_hide_content: !!input.hideContent,
        p_note: input.note?.trim() || null,
      });
      if (error) throw new SafetyError(translateSafetyError(error.message));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['content-reports'] }),
  });
}
